import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Close16,
  Automatic16,
  OverflowMenuVertical16
} from '@carbon/icons-react';
import { ViewingEmail, SCREEN_SIZE, APP_URL, USERSROLE } from 'types';
import {
  Loading,
  Button,
  Tag,
  TooltipIcon,
  DatePicker,
  Modal
} from 'carbon-components-react';
import {
  toLocalDateFormat,
  downloadFile,
  formFullAddress
} from 'utilities/helper.util';
import { useComponentMount, useEmptyCallback } from 'utilities/hooks.util';
import {
  selectScreenSize,
  selectIsShowHideTriggered,
  currentUser,
  selectorAlertNotificationShowHide
} from 'redux/session/selectors';
import { selectHeaderPanelExpand } from 'redux/app/selectors';
import { addAlert, updateUserStart } from 'redux/session/actions';
import { resetCustomTeamBaseMap } from 'redux/team/actions';
import { useSelector, useDispatch } from 'react-redux';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import {
  getEnquiryDetailsStart,
  setEnquiryReferralsDetails,
  setPreviewContent,
  editEnquiryAutoRenewStart,
  editEnquiryAutoRenewReset,
  archiveEnquiryStart,
  getEnquiryAutoRenewSeriesStart,
  addEventStart
} from './localStore/actions';
import { PreviewContent } from './localStore/types';
import StatusIcon from '../StatusIcon';
import AutoRenew from './autoRenew';
import MoveEnquiryToTeam from './moveEnquiryToTeam';
import ArchiveSeries from './archiveSeries';
import PermalinkSection from './permalinkSection';
import { translateEnquiryStatus } from 'utilities/helper.util';
import { getListAnnouncementsStart } from 'redux/announcement';
import { geojsonToArcGIS } from '@terraformer/arcgis';

const adminRole: Array<string> = [USERSROLE.ORGADMIN, USERSROLE.MANAGER];

const getDocType = (
  contentType: string = '',
  fileName: string = ''
): string => {
  if (contentType.match(/image/)) {
    return 'Image';
  } else if (contentType.match(/pdf/) || fileName.match(/\.pdf/)) {
    return 'PDF';
  } else if (contentType.match(/vnd\.(?:(?:openxml)|(?:ms-))/i)) {
    return 'Document';
  } else {
    return 'File';
  }
};

const EnquiryDetails: React.FC<Props> = ({
  handleCloseIconClick,
  enquiryIdDetails,
  setEnquiryIdDetails,
  isAnonymousView = false,
  anonymousEnquiryDetailsData,
  vhToken,
  plToken,
  setSearchText,
  setSearchInputValue,
  refreshMap,
  setIsListView,
  sentinelSuccesfulLogin
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<DatePicker>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const waitingUpdateEmail = useRef<number | undefined>();
  const waitingArchivingEnquiry = useRef<boolean>(false);
  const screenSize = useSelector(selectScreenSize);
  const isShowHideTriggered = useSelector(selectIsShowHideTriggered);
  const user = useSelector(currentUser);
  const headerPanelExpand: any = useSelector(selectHeaderPanelExpand);
  const alertNotificationToggle = useSelector(
    selectorAlertNotificationShowHide
  );
  const userId = user.id;
  const userRole = user.role_type_actual;
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const {
    currentAttachment,
    enquiryDetails,
    enquiryReferrals,
    isLoadingEnquiryDetails,
    combinedPDFDownloadURL,
    isDownloadingCombinedPDF,
    combinedZIPDownloadData,
    isDownloadingCombinedZIP,
    partialZipDownloadData,
    isDownloadingPartialZip,
    enquiryAutoRenew,
    editingEnquiryAutoRenew,
    enquiryAutoRenewSeries,
    isArchivingEnquiry,
    archivedEnquiry
  } = localStore;

  const [referrals, setReferrals] = useState<any[]>([]);
  const [filteredEnquiryDetails, setFilteredEnquiryDetails] = useState<any>('');
  const [enquiryDetailsData, setEnquiryDetailsData] = useState<any>('');
  const [selectedEmail, setSelectedEmail] = useState<
    ViewingEmail | undefined
  >();
  const [showAutoRenew, setShowAutoRenew] = useState<boolean>(false);
  const [autoRenewData, setAutoRenewData] = useState<any>('');
  const [needRefresh, setNeedRefresh] = useState<boolean>(false);
  const [showMoveEnquiryToTeam, setShowMoveEnquiryToTeam] =
    useState<boolean>(false);
  const [showAutoRenewItemMenu, setShowAutoRenewItemMenu] =
    useState<boolean>(false);
  const [showEnableAutoRenewMenu, setShowEnableAutoRenewMenu] =
    useState<boolean>(false);
  const [showARIcon, setShowARIcon] = useState<boolean>(false);
  const [showArchiveSeries, setShowArchiveSeries] = useState<boolean>(false);
  const [seriesIsArchive, setSeriesIsArchive] = useState<boolean>(false);
  const [showPermalink, setShowPermalink] = useState<boolean>(false);
  const [enquiryId, setEnquiryId] = useState<number>(0);
  const [showResponseCollationIFrame, setShowResponseCollationIFrame] =
    useState<boolean>(false);
  const [iframeLoading, setIframeLoading] = useState<boolean>(true);
  const [showMoveEnquiryItemMenu, setShowMoveEnquiryItemMenu] =
    useState<boolean>(false);
  const [canArchive, setCanArchive] = useState<boolean>(false);

  const previewDocument = useEmptyCallback(
    (content: PreviewContent | undefined) => {
      localDispatch(setPreviewContent(content));
    }
  );

  const getARSeriesData = useEmptyCallback((enquiryId: number) => {
    localDispatch(getEnquiryAutoRenewSeriesStart({ enquiryId }));
  });

  const archiveEnquiry = (
    enquiryId: number,
    enquiryIdArray: number[],
    isArchive: boolean
  ) => {
    setNeedRefresh(true);
    localDispatch(
      archiveEnquiryStart({ enquiryId, enquiryIdArray, isArchive })
    );
  };

  const handleAutoRenewingClick = () => {
    const autoRenewData = {
      enquiryId: filteredEnquiryDetails.id,
      enquiryLodgedAt: filteredEnquiryDetails.enquiry_at,
      jobStartAt: filteredEnquiryDetails.start_at,
      jobEndAt: filteredEnquiryDetails.is_edit
        ? filteredEnquiryDetails.end_at_edited
        : filteredEnquiryDetails.end_at,
      autoRenew: filteredEnquiryDetails.is_edit
        ? filteredEnquiryDetails.auto_renew_edited
        : filteredEnquiryDetails.auto_renew,
      autoRenewDate:
        filteredEnquiryDetails.is_edit && !isAnonymousView
          ? filteredEnquiryDetails.auto_renew_date_edited
          : filteredEnquiryDetails.auto_renew_date,
      autoRenewDone: filteredEnquiryDetails.auto_renew_done,
      autoRenewEnquiryId: filteredEnquiryDetails.auto_renew_enquiry_id,
      autoRenewEvery: filteredEnquiryDetails.is_edit
        ? filteredEnquiryDetails.auto_renew_every_edited
        : filteredEnquiryDetails.auto_renew_every,
      autoRenewUntil: filteredEnquiryDetails.is_edit
        ? filteredEnquiryDetails.auto_renew_until_edited
        : filteredEnquiryDetails.auto_renew_until,
      isSentinel: filteredEnquiryDetails.is_sentinel,
      isArchived: !!filteredEnquiryDetails.archived_at,
      internalCreate: filteredEnquiryDetails.internal_create,
      isAnonymousView,
      isExpired: filteredEnquiryDetails.is_expired,
      isEdit: filteredEnquiryDetails.is_edit,
      isNewAutoRenew: showEnableAutoRenewMenu
    };
    setAutoRenewData(autoRenewData);
    setShowAutoRenew(true);
  };

  const handleSaveEditAutoRenew = (values: any) => {
    const params = {
      enquiryId: filteredEnquiryDetails.id,
      editId: filteredEnquiryDetails.id,
      isDuplicate: false,
      isEdit: true,
      autoRenew: values.autoRenew,
      autoRenewEvery: values.autoRenewEvery,
      autoRenewUntil: values.autoRenewUntil,
      completionDate: values.jobEndDate
    };
    setNeedRefresh(true);
    localDispatch(editEnquiryAutoRenewStart(params));
  };

  const handleAutoRenewClose = () => {
    setAutoRenewData(undefined);
    setShowAutoRenew(false);
    localDispatch(editEnquiryAutoRenewReset());
  };

  const handleMoveEnquiryToTeam = () => {
    setShowMoveEnquiryToTeam(true);
  };

  const handleMoveEnquiryToTeamClose = () => {
    setShowMoveEnquiryToTeam(false);
  };

  const handleRefreshCurrentTeamView = () => {
    handleCloseIconClick(true);
  };

  const handleGoToEnquiry = (
    projectId: number,
    enquiryId: number,
    jobId: number | string
  ) => {
    handleCloseIconClick(false);
    // set the new team
    dispatch(resetCustomTeamBaseMap());
    dispatch(
      updateUserStart({ default_project: projectId > 0 ? projectId : null })
    );
    // set the details panel to open the enquiry
    setTimeout(() => {
      setSearchText && setSearchText(jobId);
    }, 500);
    setTimeout(() => {
      setSearchInputValue && setSearchInputValue(jobId);
    }, 750);
    setTimeout(() => {
      setEnquiryIdDetails && setEnquiryIdDetails(enquiryId);
    }, 1000);
  };

  const handleArchiveSeriesClick = (isArchive: boolean) => {
    setSeriesIsArchive(isArchive);
    setShowArchiveSeries(showArchiveSeries ? false : true);
  };

  const handleShareLinkButtonClick = (details: any) => {
    setShowPermalink(true);
    const params = {
      event_type: 'SHARE_BUTTON_CLICK',
      details
    };
    localDispatch(addEventStart(params));
  };

  const goToEnquiry = (shape: any) => {
    if (shape) {
      const { loadedMapModules } = window;
      const { Polygon } = loadedMapModules;
      const geometry = geojsonToArcGIS(shape);
      const polygon = new Polygon(geometry);
      window.loadedView.goTo(polygon.extent);
    }
  };

  const setFullReferrals = useEmptyCallback((data) => {
    localDispatch(setEnquiryReferralsDetails(data));
  });

  const [showActionsMenu, setShowActionsMenu] = useState<boolean>(false);

  const toggleActionsMenu = () => {
    setShowActionsMenu(!showActionsMenu);
  };

  const handleArchiveEnquiry = (enquiryId: number, isArchive: boolean) => {
    if (showAutoRenewItemMenu && enquiryAutoRenewSeries?.length > 1) {
      handleArchiveSeriesClick(isArchive);
    } else {
      archiveEnquiry(enquiryId, [], isArchive);
    }
  };

  const handleActionsMenuItemClick = (action: string) => {
    toggleActionsMenu();
    if (action === 'moveEnquiryToTeam') {
      handleMoveEnquiryToTeam();
    } else if (action === 'duplicateEnquiry') {
      history.push({
        pathname: APP_URL.NEW_ENQUIRY,
        state: {
          duplicateFrom: String(enquiryId)
        }
      });
    } else if (action === 'viewOnMap') {
      if (!isLoadingEnquiryDetails && enquiryDetailsData) {
        setIsListView && setIsListView(false);
        window.viewEnquiryOnMap = enquiryDetailsData.shape;
        if (window.loadedView && window.loadedView.ready === true) {
          goToEnquiry(enquiryDetailsData.shape);
        } else {
          window.viewEnquiryOnMap = enquiryDetailsData.shape;
        }
        handleCloseIconClick();
      }
    } else if (action === 'shareLink') {
      handleShareLinkButtonClick({ enquiryId });
    } else if (action === 'archiveEnquiry') {
      const isArchive = filteredEnquiryDetails.archived_at ? false : true;
      handleArchiveEnquiry(enquiryId, isArchive);
    } else if (action === 'autoRenewProperties') {
      handleAutoRenewingClick();
    }
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
  };

  useEffect(() => {
    if (isAnonymousView && anonymousEnquiryDetailsData) {
      const assetOwners = anonymousEnquiryDetailsData.Assetowner.map(
        (assetOwner: any) => {
          return {
            ...assetOwner,
            isLoading: true
          };
        }
      );
      setFullReferrals(assetOwners);
      setEnquiryDetailsData(anonymousEnquiryDetailsData);
    } else if (!isAnonymousView && enquiryDetails) {
      if (typeof enquiryDetails === 'string') {
        handleCloseIconClick();
        dispatch(addAlert('error', enquiryDetails));
      } else {
        setEnquiryDetailsData(enquiryDetails);
      }
    }
  }, [
    anonymousEnquiryDetailsData,
    enquiryDetails,
    isAnonymousView,
    handleCloseIconClick,
    setFullReferrals,
    dispatch
  ]);

  useEffect(() => {
    if (!isDownloadingCombinedPDF && combinedPDFDownloadURL) {
      window.location.href = combinedPDFDownloadURL;
    }
  }, [isDownloadingCombinedPDF, combinedPDFDownloadURL]);

  useEffect(() => {
    if (!isDownloadingCombinedZIP && combinedZIPDownloadData) {
      const { blobData, fileName } = combinedZIPDownloadData;
      downloadFile(blobData, fileName, 'application/zip');
    }
  }, [isDownloadingCombinedZIP, combinedZIPDownloadData]);

  useEffect(() => {
    if (!isDownloadingPartialZip && partialZipDownloadData) {
      const { blobData, fileName } = partialZipDownloadData;
      downloadFile(blobData, fileName, 'application/zip');
    }
  }, [isDownloadingPartialZip, partialZipDownloadData]);

  useEffect(() => {
    if (!isLoadingEnquiryDetails && enquiryDetailsData) {
      setEnquiryId(enquiryDetailsData.id);

      const isExpired = enquiryDetailsData.is_expired;
      const archivedAt = enquiryDetailsData.archived_at;
      const status = isExpired
        ? 'expired'
        : archivedAt
        ? 'archived'
        : enquiryDetailsData.status;
      const address = formFullAddress(enquiryDetailsData.address);
      const filteredEnquiryDetails = {
        ...enquiryDetailsData,
        status,
        address
      };
      setFilteredEnquiryDetails(filteredEnquiryDetails);
      const enquiryUserId = filteredEnquiryDetails.user_id;
      setShowMoveEnquiryItemMenu(
        (userRole && adminRole.includes(userRole)) ||
          (userRole === 'User' && enquiryUserId === userId)
      );
      setCanArchive(
        (userRole && adminRole.includes(userRole)) ||
          enquiryDetailsData.user_id === userId
      );

      let showARIcon = false;
      let showARProperties = false;
      let showEnableAR = false;

      if (enquiryDetailsData.is_edit) {
        if (
          enquiryDetailsData.internal_create &&
          enquiryDetailsData.auto_renew_edited &&
          enquiryDetailsData.auto_renew_until_edited
        ) {
          if (!enquiryDetailsData.archived_at) {
            showARIcon = true;
          }
          showARProperties = true;
        } else if (
          enquiryDetailsData.internal_create &&
          !enquiryDetailsData.archived_at &&
          (enquiryDetailsData.auto_renew_until_edited ||
            enquiryDetailsData.auto_renew_until) &&
          !enquiryDetailsData.is_expired
        ) {
          showEnableAR = true;
        }
      } else {
        // this is for equiry with AR created in sentinel and also created with AR (originally)
        if (
          enquiryDetailsData.auto_renew ||
          (!enquiryDetailsData.internal_create &&
            enquiryDetailsData.auto_renew_until)
        ) {
          if (!enquiryDetailsData.archived_at) {
            showARIcon = true;
          }
          showARProperties = true;
        }
      }

      if (!isAnonymousView && enquiryDetailsData.internal_create) {
        if (
          !enquiryDetailsData.is_edit &&
          !enquiryDetailsData.auto_renew &&
          !enquiryDetailsData.archived_at &&
          !enquiryDetailsData.is_expired
        ) {
          showEnableAR = true;
        }
      }
      setShowAutoRenewItemMenu(showARProperties);
      setShowARIcon(showARIcon);
      setShowEnableAutoRenewMenu(showEnableAR);
    }
  }, [
    isLoadingEnquiryDetails,
    enquiryDetailsData,
    isAnonymousView,
    plToken,
    vhToken,
    userId,
    userRole
  ]);

  useEffect(() => {
    if (enquiryReferrals.length > 0) {
      setReferrals(enquiryReferrals);
    }
  }, [enquiryReferrals]);

  useEffect(() => {
    if (!isLoadingEnquiryDetails && enquiryId && enquiryId !== 0) {
      const queryParams = {
        enquiryId: enquiryId
      };
      dispatch(getListAnnouncementsStart(queryParams));
    }
  }, [isLoadingEnquiryDetails, enquiryId, dispatch]);

  useEffect(() => {
    if (currentAttachment) {
      previewDocument({
        type: getDocType(
          currentAttachment?.content_type,
          currentAttachment?.filename
        ),
        title: currentAttachment?.presentation_name || 'Unknown',
        content: currentAttachment.url
      });
    }
  }, [currentAttachment, previewDocument]);

  useEffect(() => {
    if (selectedEmail === undefined) {
      waitingUpdateEmail.current = undefined;
    }
  }, [selectedEmail]);

  useEffect(() => {
    if (waitingUpdateEmail.current && referrals.length > 0) {
      referrals.forEach((r: any) => {
        if (r.id === waitingUpdateEmail.current) {
          setSelectedEmail({
            seqNo: r.seq_no,
            assetOwnerContact: r.phone !== 'N/A' ? r.phone : undefined,
            id: r.id,
            name: r.authority_name,
            bodies: r.Mailbody,
            uploadedFiles: r.Directattachment
          });
        }
      });
    }
  }, [referrals]);

  useEffect(() => {
    !isAnonymousView &&
      showAutoRenewItemMenu &&
      getARSeriesData(enquiryIdDetails);
  }, [
    showAutoRenewItemMenu,
    isAnonymousView,
    enquiryIdDetails,
    getARSeriesData
  ]);

  const renderMapAgain = useEmptyCallback(
    () => typeof refreshMap === 'function' && refreshMap()
  );

  useEffect(() => {
    if (isArchivingEnquiry && !waitingArchivingEnquiry.current) {
      waitingArchivingEnquiry.current = true;
    }
    if (!isArchivingEnquiry && waitingArchivingEnquiry.current) {
      waitingArchivingEnquiry.current = false;
      renderMapAgain();
    }
  }, [isArchivingEnquiry, renderMapAgain]);

  useEffect(() => {
    if (sentinelSuccesfulLogin) {
      setShowResponseCollationIFrame(true);
    }
  }, [sentinelSuccesfulLogin]);

  useEffect(() => {
    if (headerPanelExpand?.isOpen) {
      !isAnonymousView && handleCloseIconClick();
    }
  }, [headerPanelExpand, isAnonymousView, handleCloseIconClick]);

  useComponentMount(() => {
    const params = { enquiryId: enquiryIdDetails };
    !isAnonymousView && localDispatch(getEnquiryDetailsStart(params));
  });

  return (
    <>
      <div
        className={`${
          alertNotificationToggle ? 'noti-alert-notification-rc-modal' : ''
        } enquiry-details-rc-modal`}
      >
        <section
          ref={containerRef}
          className={`enquiry-details-rc-modal-main ${
            isAnonymousView ? 'anonymous-view' : ''
          }
          ${isShowHideTriggered ? 'locate-theme' : ''}
          `}
        >
          {enquiryIdDetails !== 0 &&
            !isLoadingEnquiryDetails &&
            filteredEnquiryDetails && (
              <div className="modal-container">
                <div className="modal-header-container">
                  <div className="modal-header-container-top">
                    <div className="modal-header-container-top-left">
                      <Tag
                        type="purple"
                        className={`modal-title-tag ${filteredEnquiryDetails.status.toLowerCase()}`}
                      >
                        {`Job ${filteredEnquiryDetails.external_id}`}
                      </Tag>
                      <div className="auto-renew-icon">
                        {showARIcon && (
                          <TooltipIcon
                            tooltipText={`Auto-renews until ${toLocalDateFormat(
                              filteredEnquiryDetails.is_edit
                                ? filteredEnquiryDetails.auto_renew_until_edited
                                : filteredEnquiryDetails.auto_renew_until
                            )}`}
                            align={'center'}
                          >
                            <Automatic16 />
                          </TooltipIcon>
                        )}
                      </div>
                      <div className="expiry-date">
                        Expires{' '}
                        {toLocalDateFormat(filteredEnquiryDetails.expire_at)}
                      </div>
                    </div>
                    <div className="modal-header-container-top-right">
                      {screenSize !== SCREEN_SIZE.SM && (
                        <div
                          className={`enquiry-status ${filteredEnquiryDetails.status}`}
                        >
                          <StatusIcon
                            status={filteredEnquiryDetails.status}
                            isIgnore={filteredEnquiryDetails.is_ignore}
                            archivedAt={filteredEnquiryDetails.archived_at}
                          />
                          &nbsp;&nbsp;
                          {translateEnquiryStatus(
                            filteredEnquiryDetails.status,
                            filteredEnquiryDetails.archived_at,
                            filteredEnquiryDetails.is_ignore,
                            true
                          )}
                        </div>
                      )}

                      {((isAnonymousView && showAutoRenewItemMenu) ||
                        !isAnonymousView) && (
                        <div className="actions-menu">
                          <Button
                            renderIcon={OverflowMenuVertical16}
                            className={`actions-menu-button ${
                              showActionsMenu
                                ? 'actions-menu-button-active'
                                : ''
                            }`}
                            onClick={() => toggleActionsMenu()}
                            size="sm"
                            kind="tertiary"
                            hasIconOnly={screenSize === SCREEN_SIZE.SM}
                          >
                            {screenSize === SCREEN_SIZE.SM ? '' : 'Actions'}
                          </Button>
                          {showActionsMenu && (
                            <div className="actions-menu-container">
                              {showAutoRenewItemMenu && (
                                <div
                                  className="menu-item"
                                  onClick={() =>
                                    handleActionsMenuItemClick(
                                      'autoRenewProperties'
                                    )
                                  }
                                >
                                  <div className="menu-item-label">
                                    Auto-renew properties
                                  </div>
                                </div>
                              )}
                              {!isAnonymousView && (
                                <>
                                  {showMoveEnquiryItemMenu && (
                                    <div
                                      className="menu-item"
                                      onClick={() =>
                                        handleActionsMenuItemClick(
                                          'moveEnquiryToTeam'
                                        )
                                      }
                                    >
                                      <div className="menu-item-label">
                                        Move enquiry to team
                                      </div>
                                    </div>
                                  )}
                                  <div
                                    className="menu-item"
                                    onClick={() =>
                                      handleActionsMenuItemClick(
                                        'duplicateEnquiry'
                                      )
                                    }
                                  >
                                    <div className="menu-item-label">
                                      Duplicate enquiry
                                    </div>
                                  </div>
                                  {canArchive && (
                                    <div
                                      className="menu-item"
                                      onClick={() =>
                                        handleActionsMenuItemClick(
                                          'archiveEnquiry'
                                        )
                                      }
                                    >
                                      <div className="menu-item-label">
                                        {filteredEnquiryDetails.archived_at
                                          ? 'Restore'
                                          : 'Archive'}{' '}
                                        enquiry
                                      </div>
                                    </div>
                                  )}
                                  {filteredEnquiryDetails.permalink && (
                                    <div
                                      className="menu-item"
                                      onClick={() =>
                                        handleActionsMenuItemClick('shareLink')
                                      }
                                    >
                                      <div className="menu-item-label">
                                        Share link
                                      </div>
                                    </div>
                                  )}

                                  <div
                                    className="menu-item"
                                    onClick={() =>
                                      handleActionsMenuItemClick('viewOnMap')
                                    }
                                  >
                                    <div className="menu-item-label-no-border">
                                      View on map
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {!isAnonymousView && (
                        <div
                          className="close-button-body"
                          onClick={() => handleCloseIconClick(needRefresh)}
                        >
                          <Close16 />
                        </div>
                      )}
                    </div>
                  </div>
                  {screenSize === SCREEN_SIZE.SM && (
                    <div
                      className={`modal-header-container-bottom enquiry-status ${filteredEnquiryDetails.status}`}
                    >
                      <StatusIcon
                        status={filteredEnquiryDetails.status}
                        isIgnore={filteredEnquiryDetails.is_ignore}
                        archivedAt={filteredEnquiryDetails.archived_at}
                      />
                      &nbsp;&nbsp;
                      {translateEnquiryStatus(
                        filteredEnquiryDetails.status,
                        filteredEnquiryDetails.archived_at,
                        filteredEnquiryDetails.is_ignore,
                        true
                      )}
                    </div>
                  )}

                  {/* Move enquiry to teams modal */}
                  <Modal
                    passiveModal
                    onRequestClose={() => {
                      handleMoveEnquiryToTeamClose();
                    }}
                    open={showMoveEnquiryToTeam}
                    className={`move-enquiry-to-team-modal`}
                    modalHeading="Move enquiry to team"
                  >
                    <MoveEnquiryToTeam
                      onRefreshTeamView={handleRefreshCurrentTeamView}
                      enquiryTeamId={filteredEnquiryDetails.project_id}
                      enquiryId={filteredEnquiryDetails.id}
                      enquiryUserId={filteredEnquiryDetails.user_id}
                      jobId={filteredEnquiryDetails.external_id}
                      refreshMap={refreshMap}
                      closeDetailsPanel={handleCloseIconClick}
                      setNeedRefresh={setNeedRefresh}
                      onGoToEnquiry={handleGoToEnquiry}
                      isAutoRenew={showAutoRenewItemMenu}
                      enquiryAutoRenewSeries={enquiryAutoRenewSeries}
                      screenSize={screenSize}
                    />
                  </Modal>

                  {/* Permalink modal */}
                  <Modal
                    passiveModal
                    onRequestClose={() => {
                      setShowPermalink(false);
                    }}
                    open={showPermalink}
                    className={`permalink-modal`}
                    modalHeading="Share link"
                  >
                    {showPermalink && (
                      <PermalinkSection
                        url={filteredEnquiryDetails.permalink}
                      />
                    )}
                  </Modal>

                  {/* Archive series modal */}
                  <Modal
                    passiveModal
                    onRequestClose={() => {
                      setShowArchiveSeries(false);
                    }}
                    open={showArchiveSeries}
                    className={`archive-enquiry-modal`}
                    modalHeading="Archive enquiry"
                  >
                    {enquiryAutoRenewSeries?.length > 1 && (
                      <ArchiveSeries
                        onClose={handleArchiveSeriesClick}
                        onRefreshTeamView={handleRefreshCurrentTeamView}
                        enquiryId={filteredEnquiryDetails.id}
                        enquiryAutoRenewSeries={enquiryAutoRenewSeries}
                        isArchive={seriesIsArchive}
                        archiveEnquiry={archiveEnquiry}
                        isArchivingEnquiry={isArchivingEnquiry}
                        archivedEnquiry={archivedEnquiry}
                        screenSize={screenSize}
                      />
                    )}
                  </Modal>

                  {/* Auto-renew modal */}
                  <Modal
                    passiveModal
                    onRequestClose={() => {
                      handleAutoRenewClose();
                    }}
                    open={showAutoRenew}
                    className={`auto-renew-modal`}
                    modalHeading="Auto-renew properties"
                  >
                    {autoRenewData && (
                      <AutoRenew
                        autoRenewData={autoRenewData}
                        onClose={handleAutoRenewClose}
                        onSaveAutoRenew={handleSaveEditAutoRenew}
                        calendarRef={calendarRef}
                        editingEnquiryAutoRenew={
                          editingEnquiryAutoRenew || false
                        }
                        enquiryAutoRenew={enquiryAutoRenew}
                      />
                    )}
                  </Modal>
                  {/* End all the modals */}
                </div>
                <div className={`modal-content-container`}>
                  {iframeLoading && (
                    <Loading
                      withOverlay={true}
                      className="iframe-content-loading"
                    />
                  )}
                  {showResponseCollationIFrame && (
                    <iframe
                      id="responseCollationIframe"
                      ref={iframeRef}
                      title="sentinel-response-collation"
                      className={`modal-content-iframe`}
                      onLoad={handleIframeLoad}
                      src={`${process.env.REACT_APP_SENTINEL_WEB_URL}/enquiries/${filteredEnquiryDetails.sentinel_id}?origin=swxlocate`}
                    />
                  )}
                </div>

                {/* <div className="response-collation-iframe-container"></div> */}
              </div>
            )}
          {(enquiryIdDetails === 0 || isLoadingEnquiryDetails) && (
            <div className="loading-details">
              <Loading withOverlay={true} />
            </div>
          )}
        </section>
      </div>
    </>
  );
};

EnquiryDetails.displayName = 'EnquiryDetails';

type Props = {
  handleCloseIconClick?: any;
  enquiryIdDetails?: number;
  setEnquiryIdDetails?: Function;
  isListView: boolean;
  isAnonymousView?: boolean;
  anonymousEnquiryDetailsData?: any;
  vhToken?: string;
  plToken?: string;
  setSearchText?: Function;
  setSearchInputValue?: Function;
  setIsListView?: Function;
  refreshMap?: Function;
  sentinelSuccesfulLogin?: boolean;
};

export default React.memo(EnquiryDetails);
